import { inject, Injectable, signal } from "@angular/core";
import { Router } from "@angular/router";
import { HttpClientService } from "./http-client.service";
import { environment } from "src/environments/environment";
import { HttpErrorResponse, HttpParams } from "@angular/common/http";
import { AuthInfo } from "@core/models/dto/AuthInfoDto";
import { Role } from "@core/enums/Role.enum";
import { catchError, tap, throwError } from "rxjs";

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private httpClient = inject(HttpClientService);
  private router = inject(Router);
  isLogged = signal<boolean>(localStorage.getItem('role') === Role.USER && !!localStorage.getItem('sessionExpirationTime'));

  private loginAsGuest$(){
    return this.httpClient.post<AuthInfo>(undefined ,'auth/guesttoken')
    .pipe(
      tap(authInfo => this.setAuthorizedState(authInfo))
    );
  }

  private refreshCookie$(){
    return this.httpClient.post<AuthInfo>(undefined, 'auth/refresh')
    .pipe(
      tap(authInfo => {
        if (authInfo) this.setAuthorizedState(authInfo);
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) this.resetState();
        return throwError(()=> error);
      })
    )
  }

  private login$(params:HttpParams){
    return this.httpClient.post<AuthInfo>(undefined, 'auth/login', params)
    .pipe(
      tap(authInfo => {
        if (authInfo) this.setAuthorizedState(authInfo);
      }),
      catchError(error => {
        this.resetState();
        return throwError(()=> error);
      })
    )
  }

  private logout$(){
    return this.httpClient.delete('auth/logout');
  }

  getAuthorization$(){
    const storageExpTime = localStorage.getItem('sessionExpirationTime') ?? 0
    const role = localStorage.getItem('role') ?? 'Guest'
    if(!storageExpTime || (role === 'Guest' && this.sessionExpired())){
      return this.loginAsGuest$();
    }
    return this.refreshCookie$();
  }

  initiateUL(){
    const state = crypto.getRandomValues(new Uint32Array(1)).at(0)!.toString();
    sessionStorage.setItem('state', state);
    window.location.href = environment.unified_login + state;
  }

  initiateULSignup(){
    window.location.href = environment.unified_signup;
  }

  isValidState(state:string){
    const isValid =  state === sessionStorage.getItem('state');
    sessionStorage.removeItem('state');
    return isValid;
  }


  setAuthorizedState(authInfo: AuthInfo){
    this.setLocalStorageItems(authInfo);
    const isUser = authInfo.role === Role.USER;
    this.isLogged.set(isUser);
    const isAuth = this.router.url.includes('/authorize');
    const path = isUser && isAuth ? 'profile/dashboard' : this.router.url;
    this.router.navigate([path]);
  }

  resetState(){
    this.isLogged.set(false);
    this.router.navigate(['']);
    this.clearLocalStorageItems();
    this.loginAsGuest$().subscribe();
  }

  loginAsGuest(){
    this.loginAsGuest$().subscribe();
  }

  login(code: string) {
    const params = new HttpParams().set('accessCode', code);
    this.login$(params).subscribe();
  }

  logout(){
    this.logout$().subscribe(() => this.resetState());
  }

  private setLocalStorageItems(authInfo: AuthInfo){
    localStorage.setItem('sessionExpirationTime', authInfo.accessTokenExpirationTime);
    localStorage.setItem('role', authInfo.role);
  }

  private clearLocalStorageItems(){
    localStorage.removeItem('sessionExpirationTime');
    localStorage.removeItem('role');
  }

  private sessionExpired(): boolean{
    const storageExpTime = localStorage.getItem('sessionExpirationTime') ?? 0;
    if(!storageExpTime) return true;
    return  new Date(storageExpTime).getTime() < new Date(Date.now()).getTime();
  }
}
